/**
 * What the check found before homes are added to a mailshot (EST-169, slice 3): the counts
 * first, then each class of listed home with one action for the whole class, then each home
 * with both addresses as a letter would print them and its own choice.
 *
 * Presentational. The answer is the server's (checkMailshotAdd); the choices are the
 * caller's state. The default for every listed home is "leave it out"; a home that matches
 * do not contact has no choice at all.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Badge } from "./Badge";
import { Button } from "./Button";
import { Select } from "./Form";
import InfoNotice from "./InfoNotice";
import { KEEP_ORDER_WORDS, MAX_GROUP_SEARCHED } from "../../models/addChoices";
import { byClass, choicesFor, summaryOf, notesOf } from "../../utils/addCheckFlow";
import "./AddCheckReview.scss";

const WHERE = { mailshot: "Already in this mailshot", selection: "In this selection", "do-not-contact": "Marked do not contact" };
const lines = (printed) => (printed || []).join("\n");
const SHOWN_AT_FIRST = 6;

export function AddCheckReview({ answer, choices, onChoose, onChooseClass }) {
  const [open, setOpen] = useState({});
  const check = answer.check;
  const classes = byClass(check);
  const notes = notesOf(answer);
  const unsearched = check.unsearched || [];

  return (
    <div className="add-check" data-testid="add-check">
      <p className="add-check__summary" data-testid="add-check-summary">{summaryOf(answer)}</p>
      {notes.map((note) => <p key={note} className="add-check__note">{note}</p>)}

      {unsearched.map((u) => {
        const out = u.homes.filter((h) => choices[h.id] === "out").length;
        const key = `unsearched-${u.group}`;
        const shown = open[key] ? u.homes : u.homes.slice(0, SHOWN_AT_FIRST);
        return (
          <section key={key} className="add-check__class" data-testid={`add-check-unsearched-${u.group}`}>
            <div className="add-check__head">
              <strong>Too many alike to work out</strong> <Badge variant="warning" size="small">{u.homes.length.toLocaleString("en-GB")}</Badge>
              <span className="add-check__actions">
                <Button variant="secondary" size="small" onClick={() => u.homes.forEach((h, i) => onChoose(h.id, i === 0 ? "in" : "out"))} disabled={out === u.homes.length - 1 && choices[u.homes[0].id] !== "out"}>Leave all but one out</Button>
                <Button variant="secondary" size="small" onClick={() => u.homes.forEach((h) => onChoose(h.id, "in"))} disabled={out === 0}>Add them all</Button>
              </span>
            </div>
            <p className="add-check__words">
              These {u.homes.length.toLocaleString("en-GB")} homes in one postcode read as each other in more ways than the check works out (a number and
              its flats, for example): it works a group out up to {MAX_GROUP_SEARCHED} records, and this one has more. NOTHING of them is left out: all are
              being added unless you leave one out here. They need your eye.
              {out ? ` ${out.toLocaleString("en-GB")} left out by your choice.` : ""}
            </p>
            <p className="add-check__words">"Leave all but one out" keeps the first shown here, which is the first by this order: {KEEP_ORDER_WORDS}</p>
            {shown.map((h) => (
              <div key={h.id} className="add-check__home" data-testid={`add-check-home-${h.id}`}>
                <div className="add-check__sides"><div className="add-check__side"><div className="add-check__printed">{lines(h.printed)}</div></div></div>
                <Select value={choices[h.id] === "out" ? "out" : "in"} onChange={(e) => onChoose(h.id, e.target.value === "out" ? "out" : "in")} options={[{ value: "in", label: "Add it" }, { value: "out", label: "Leave it out" }]} placeholder="" size="small" aria-label={`What to do with ${(h.printed || [])[0] || h.id}`} />
              </div>
            ))}
            {u.homes.length > SHOWN_AT_FIRST && (
              <Button variant="link" size="small" onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}>{open[key] ? "Show fewer" : `Show all ${u.homes.length.toLocaleString("en-GB")}`}</Button>
            )}
          </section>
        );
      })}

      {classes.map((c) => {
        const chosen = c.entries.filter((l) => choices[l.id] === "add" || choices[l.id] === "instead").length;
        const shown = open[c.key] ? c.entries : c.entries.slice(0, SHOWN_AT_FIRST);
        return (
          <section key={c.key} className="add-check__class" data-testid={`add-check-class-${c.key}`}>
            <div className="add-check__head">
              <strong>{c.title}</strong> <Badge variant={c.release ? "warning" : "danger"} size="small">{c.entries.length.toLocaleString("en-GB")}</Badge>
              {c.release && (
                <span className="add-check__actions">
                  <Button variant="secondary" size="small" onClick={() => onChooseClass(c.key, "leave")} disabled={chosen === 0}>Leave all of these out</Button>
                  <Button variant="secondary" size="small" onClick={() => onChooseClass(c.key, "add")} disabled={c.entries.every((l) => choices[l.id] === "add")}>Add all of these anyway</Button>
                </span>
              )}
            </div>
            <p className="add-check__words">{c.words}{c.release ? ` ${chosen ? `${chosen.toLocaleString("en-GB")} to be added by your choice; ` : ""}${(c.entries.length - chosen).toLocaleString("en-GB")} left out.` : ""}</p>
            {c.key.startsWith("in-selection") && <p className="add-check__words">{KEEP_ORDER_WORDS}</p>}

            {shown.map((l) => {
              const options = choicesFor(check, l);
              return (
                <div key={l.id} className="add-check__home" data-testid={`add-check-home-${l.id}`}>
                  <div className="add-check__sides">
                    <div className="add-check__side">
                      <div className="add-check__side-title">{c.release ? "Left out unless you say" : "Not added"}</div>
                      <div className="add-check__printed">{lines(l.printed)}</div>
                    </div>
                    {l.against.map((a) => (
                      <div key={`${a.where}-${a.id}`} className="add-check__side">
                        <div className="add-check__side-title">{WHERE[a.where] || a.where}{a.where === "selection" ? (a.kept ? (choices[l.id] === "instead" ? ", left out by your choice (this one goes in instead)" : ", kept") : ", also left out") : ""}</div>
                        <div className="add-check__printed">{lines(a.printed)}</div>
                        <div className="add-check__why">The rule: {a.verdict === "same" ? "the same home" : a.verdict === "uncertain" ? `cannot tell (${a.words || a.reason})` : `a different home (${a.words || a.reason}), but too close to a do-not-contact record to add`}</div>
                      </div>
                    ))}
                  </div>
                  {options.length > 0 && (
                    <Select value={choices[l.id] === "add" || choices[l.id] === "instead" ? choices[l.id] : "leave"} onChange={(e) => onChoose(l.id, e.target.value)} options={options} placeholder="" size="small" aria-label={`What to do with ${(l.printed || [])[0] || l.id}`} />
                  )}
                </div>
              );
            })}
            {c.entries.length > SHOWN_AT_FIRST && (
              <Button variant="link" size="small" onClick={() => setOpen((o) => ({ ...o, [c.key]: !o[c.key] }))}>
                {open[c.key] ? "Show fewer" : `Show all ${c.entries.length.toLocaleString("en-GB")}`}
              </Button>
            )}
          </section>
        );
      })}

      {classes.length === 0 && unsearched.length === 0 && <InfoNotice variant="success">Nothing reads as a double among the homes compared, and nothing matches do not contact.</InfoNotice>}
    </div>
  );
}

AddCheckReview.propTypes = {
  answer: PropTypes.object.isRequired,
  choices: PropTypes.object.isRequired,
  onChoose: PropTypes.func.isRequired,
  onChooseClass: PropTypes.func.isRequired,
};

export default AddCheckReview;
